import { effect, inject, Injectable, signal } from '@angular/core';

import { UserStore } from '../models/internal/user.store';
import { chaveTool, Tool } from '../models/internal/tool-model';

/**
 * Histórico das ferramentas abertas pela busca da navbar.
 *
 * Fica em localStorage: precisa sobreviver à sessão (o valor do histórico está
 * justamente em voltar amanhã e achar o que se usou hoje), não tem por que trafegar
 * em requisição como um cookie, e é o mesmo mecanismo que o app já usa para CNPJ.
 * A chave leva o e-mail porque a máquina do balcão costuma ser de mais de um operador.
 * Um dia isso pode virar preferência no backend, aí passa a sincronizar entre
 * dispositivos — hoje não há endpoint para isso.
 */
@Injectable({ providedIn: 'root' })
export class SearchHistoryService {
    private static readonly CHAVE = 'RECENT_TOOLS';
    private static readonly LIMITE = 5;

    private readonly userStore = inject(UserStore);

    private readonly _recentes = signal<Tool[]>([]);
    public readonly recentes = this._recentes.asReadonly();

    constructor() {
        // O e-mail chega depois, do auth0. Quando chegar, recarrega do balde certo.
        effect(() => {
            this.userStore.user().email;
            this._recentes.set(this.ler());
        });
    }

    public registrar(tool: Tool): void {
        const semRepetido = this.ler().filter(t => chaveTool(t) !== chaveTool(tool));
        const atualizado = [tool, ...semRepetido].slice(0, SearchHistoryService.LIMITE);

        this._recentes.set(atualizado);

        try {
            localStorage.setItem(this.chave(), JSON.stringify(atualizado));
        } catch {
            // Cota estourada ou navegação privada: o histórico vale só nesta sessão.
            // Não é motivo para derrubar a busca.
        }
    }

    public limpar(): void {
        this._recentes.set([]);

        try {
            localStorage.removeItem(this.chave());
        } catch { }
    }

    private chave(): string {
        const email = this.userStore.user().email;

        return email ? `${SearchHistoryService.CHAVE}:${email}` : SearchHistoryService.CHAVE;
    }

    private ler(): Tool[] {
        try {
            const cru = localStorage.getItem(this.chave());
            const dados: unknown = cru ? JSON.parse(cru) : [];

            return Array.isArray(dados)
                ? dados.filter(SearchHistoryService.pareceTool).slice(0, SearchHistoryService.LIMITE)
                : [];
        } catch {
            // JSON corrompido, storage bloqueado: histórico vazio é degradação aceitável.
            return [];
        }
    }

    /** O que vem do disco é entrada não confiável: pode ter sido editada ou ficado de uma versão antiga. */
    private static pareceTool(valor: unknown): valor is Tool {
        const t = valor as Tool;

        return !!t && typeof t.title === 'string' && typeof t.link === 'string';
    }
}
